import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Sparkles } from "lucide-react";
import { Field, PrimaryBtn, inputCls, Opt } from "@/components/Common";
import { Markdown } from "@/components/Markdown";
import { API } from "@/lib/api";

const INTERESTS = ["Culture", "Adventure", "Food", "Beaches", "Nature", "Shopping", "Nightlife", "Wellness", "Spiritual"];

export default function Planner() {
  const [sp] = useSearchParams();
  const [f, setF] = useState({ destination: sp.get("destination") || "", days: +(sp.get("days") || 4), budget: "Moderate", group: "Couple", interests: ["Culture", "Food"] });
  const [plan, setPlan] = useState("");
  const [busy, setBusy] = useState(false);
  const outRef = useRef(null);

  const toggle = (i) => setF({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] });

  const generate = async (e) => {
    e.preventDefault();
    if (f.destination.trim().length < 2) return toast.error("Enter a destination");
    setBusy(true);
    setPlan("");
    setTimeout(() => outRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
    try {
      const res = await fetch(`${API}/planner/stream`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(f) });
      if (!res.ok) throw new Error("Planner request failed");
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        setPlan((p) => p + dec.decode(value, { stream: true }));
      }
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="planner-page" className="mx-auto max-w-7xl px-4 py-14 sm:px-8 lg:py-20">
      <div className="grid gap-12 lg:grid-cols-[420px_1fr]">
        <div className="lg:sticky lg:top-24 h-fit">
          <p className="eyebrow">AI itinerary planner</p>
          <h1 className="font-display mt-2 text-4xl font-bold text-[#0D1B2A] sm:text-5xl">Plan a trip in seconds.</h1>
          <p className="mt-3 text-slate-600">Day-by-day plans with costs in ₹, written for travellers starting from India.</p>
          <form onSubmit={generate} className="mt-8 space-y-4 rounded-2xl border border-[#E5E0D8] bg-white p-6">
            <Field label="Destination"><input className={inputCls} value={f.destination} onChange={(e) => setF({ ...f, destination: e.target.value })} placeholder="e.g. Kerala, Bali, Paris" data-testid="planner-destination-input" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Days"><input type="number" min={1} max={15} className={inputCls} value={f.days} onChange={(e) => setF({ ...f, days: Math.min(15, Math.max(1, +e.target.value)) })} data-testid="planner-days-input" /></Field>
              <Field label="Budget">
                <select className={inputCls} value={f.budget} onChange={(e) => setF({ ...f, budget: e.target.value })} data-testid="planner-budget-select">
                  {["Budget", "Moderate", "Luxury"].map((b) => <Opt key={b} v={b} l={b} />)}
                </select>
              </Field>
            </div>
            <Field label="Travelling as">
              <select className={inputCls} value={f.group} onChange={(e) => setF({ ...f, group: e.target.value })} data-testid="planner-group-select">
                {["Solo", "Couple", "Family", "Friends"].map((b) => <Opt key={b} v={b} l={b} />)}
              </select>
            </Field>
            <Field label="Interests">
              <div className="flex flex-wrap gap-2">
                {INTERESTS.map((i) => (
                  <button type="button" key={i} onClick={() => toggle(i)} data-testid={`planner-interest-${i.toLowerCase()}`}
                    className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${f.interests.includes(i) ? "bg-[#0D1B2A] text-white" : "bg-[#F4F0EA] text-slate-600"}`}>{i}</button>
                ))}
              </div>
            </Field>
            <PrimaryBtn type="submit" disabled={busy} className="w-full" data-testid="planner-generate-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}{busy ? "Writing your plan…" : "Generate itinerary"}
            </PrimaryBtn>
          </form>
        </div>
        <div ref={outRef} className="min-h-[60vh] rounded-3xl border border-[#E5E0D8] bg-white p-6 sm:p-10" data-testid="planner-output">
          {!plan && !busy && (
            <div className="grid h-full place-items-center text-center text-slate-400">
              <div><Sparkles className="mx-auto h-10 w-10 text-[#E07A5F]" /><p className="mt-4 font-display text-2xl text-[#0D1B2A]">Your itinerary will appear here</p></div>
            </div>
          )}
          {busy && !plan && <div className="space-y-3">{[1, 2, 3, 4].map((i) => <div key={i} className="h-5 animate-pulse rounded bg-[#F4F0EA]" style={{ width: `${90 - i * 12}%` }} />)}</div>}
          {plan && <Markdown text={plan} />}
          {plan && !busy && (
            <div className="mt-10 flex flex-wrap gap-3 border-t border-[#E5E0D8] pt-6">
              <Link to="/flights" className="rounded-full bg-[#0D1B2A] px-5 py-2.5 text-sm font-semibold text-white" data-testid="planner-book-flights">Book flights</Link>
              <Link to="/hotels" className="rounded-full border border-[#0D1B2A] px-5 py-2.5 text-sm font-semibold" data-testid="planner-book-hotels">Find hotels</Link>
              <Link to="/packages" className="rounded-full border border-[#0D1B2A] px-5 py-2.5 text-sm font-semibold" data-testid="planner-see-packages">See packages</Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
